import { format, isSameDay, startOfDay } from "date-fns";
import { Appointment, Job, Maybe } from "../../generated/nest-graphql";
import { isDealershipJob } from "../../lib/dealership";

/** The window a technician is actually held to: customer-facing when present. */
export const appointmentWindow = (appointment?: Maybe<Appointment>): { start: Date | null; end: Date | null } => {
  const rawStart = appointment?.timeWindow?.startTimeWindow ?? appointment?.startDate;
  const rawEnd = appointment?.timeWindow?.endTimeWindow ?? appointment?.endDate;
  return {
    start: rawStart ? new Date(rawStart) : null,
    end: rawEnd ? new Date(rawEnd) : null,
  };
};

/** The booked slot, which dispatch, the ops schedule and the calendar tab all place a job by. */
export const appointmentSlotStart = (appointment?: Maybe<Appointment>): Date | null => {
  const raw = appointment?.startDate ?? appointment?.timeWindow?.startTimeWindow;
  return raw ? new Date(raw) : null;
};

export const formatWindow = (start?: Date | null, end?: Date | null): string =>
  start && end ? `${format(start, "h:mmaaa")} - ${format(end, "h:mmaaa")}` : "";

/** "Monday, September 14" — spelled out, since the list has room the calendar didn't. */
export const formatDayHeading = (day: Date): string => format(day, "EEEE, MMMM d");

export const vehicleLabelOf = (job?: Maybe<Job>): string => {
  const vehicle = job?.vehicleInfo;
  return vehicle?.year ? `${vehicle.year} ${vehicle.make ?? ""} ${vehicle.model ?? ""}`.trim() : "";
};

export type ScheduleTag = "Dealer" | "Fixed" | "Rescheduled";

/**
 * Only the tags that carry a consequence for the technician: parts come from a
 * dealership, the slot cannot be moved, or the customer has already been moved once.
 */
export const tagsFor = (appointment?: Maybe<Appointment>): ScheduleTag[] => {
  const job = appointment?.job;
  const tags: ScheduleTag[] = [];
  if (isDealershipJob(job)) tags.push("Dealer");
  if (appointment?.isFixed) tags.push("Fixed");
  if ((job?.rescheduleSummary?.total ?? 0) > 0) tags.push("Rescheduled");
  return tags;
};

export type ScheduleDay = {
  key: string;
  day: Date;
  /** "Today"/"Tomorrow" when it lands there, else empty. */
  relative: string;
  appointments: Appointment[];
};

/**
 * The list covers exactly two days — the anchor and the one after it. A technician's
 * question here is "what's left today, and what am I walking into tomorrow"; anything
 * further out is what the calendar is for.
 */
export const buildScheduleDays = (appointments: Appointment[], anchor: Date): ScheduleDay[] => {
  const today = startOfDay(new Date());
  const first = startOfDay(anchor);
  const second = new Date(first);
  second.setDate(second.getDate() + 1);

  return [first, second].map((day) => {
    const forDay = appointments
      .filter((appointment) => {
        const start = appointmentSlotStart(appointment);
        return !!start && isSameDay(start, day);
      })
      .sort((a, b) => (appointmentSlotStart(a)?.getTime() ?? 0) - (appointmentSlotStart(b)?.getTime() ?? 0));

    const dayDiff = Math.round((day.getTime() - today.getTime()) / 86400000);
    return {
      key: day.toISOString(),
      day,
      relative: dayDiff === 0 ? "Today" : dayDiff === 1 ? "Tomorrow" : "",
      appointments: forDay,
    };
  });
};
